/**
 * Type Definitions for API Error Handling
 *
 * These types define error structures returned from the backend API.
 */

/**
 * API error response structure from backend
 */
export interface APIError {
  error: string; // Error type: "Validation Error", "Not Found", "Conflict", etc.
  message: string; // Human-readable error message
  details?: Record<string, any>; // Optional additional error details
}

/**
 * API error exception class
 * Wraps HTTP errors with typed error data
 */
export class APIErrorException extends Error {
  public readonly status: number;
  public readonly error: APIError;

  constructor(status: number, error: APIError) {
    super(error.message);
    this.name = 'APIErrorException';
    this.status = status;
    this.error = error;

    // Maintain proper stack trace for where error was thrown (V8 engines)
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, APIErrorException);
    }
  }

  /**
   * Check if error is a specific HTTP status code
   */
  isStatus(code: number): boolean {
    return this.status === code;
  }

  /**
   * Check if error is a validation error (400)
   */
  isValidationError(): boolean {
    return this.status === 400;
  }

  /**
   * Check if error is a not found error (404)
   */
  isNotFoundError(): boolean {
    return this.status === 404;
  }

  /**
   * Check if error is a conflict error (409)
   */
  isConflictError(): boolean {
    return this.status === 409;
  }

  /**
   * Check if error is a server error (500+)
   */
  isServerError(): boolean {
    return this.status >= 500;
  }

  /**
   * Get error details if available
   */
  getDetails(): Record<string, any> | undefined {
    return this.error.details;
  }
}
/**
 * react-query retry callback: skip retries on 404 (an unknown / wrong-org
 * resource won't materialise on retry); cap remaining retries at 2.
 */
export const skipRetryOn404 = (failureCount: number, error: unknown): boolean => {
  if (error instanceof APIErrorException && error.status === 404) {
    return false;
  }
  return failureCount < 2;
};
